import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';

import { claimRewardHubV2Api, getRewardHubV2Api } from '@flipster/apis/v1/rewardHub';
import { CompletionReward, FeeVoucherReward, GetRewardHubV2Response } from '@flipster/apis/v1/rewardHub/types';
import { FormattedMessage } from '@flipster/i18n';
import { overlayBunja } from '@flipster/overlay';
import { Dialog } from '@flipster/react/design-system/dialog/index';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorCode } from '@flipster/utils/error';
import Num from '@flipster/utils/num';
import webviewHandler from '@flipster/utils/webview-handler';

import { authBunja } from '../../shared/auth/state';
import { showErrorDialog } from '../../shared/show-error-dialog';
import ClaimModal from './section-list/claim-modal';
import { getRewardAmount } from './section-list/utils';

const SpecialSectionIndex = -1;

export const rewardHubV2Bunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const overlay = bunja.use(overlayBunja);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);

  const claimRewardHubV2MutationAtom = atomWithMutation(() => ({
    mutationKey: ['claimRewardHubV2Api'],
    mutationFn: async ({
      sectionIndex,
      missionIndex,
      completionReward,
    }: {
      sectionIndex: number;
      missionIndex: number;
      completionReward: CompletionReward;
    }) => {
      await claimRewardHubV2Api({ sectionIndex, missionIndex });
      const { data } = await store.get(rewardHubV2QueryAtom).refetch();
      const settleAt = data?.missionStatus?.sectionStatus[sectionIndex]?.regularMissionStatus[missionIndex]?.settleAt;
      return { completionReward, settleAt, feeVoucherRewardRecord: data?.feeVoucherReward ?? undefined };
    },
    onSuccess: ({ completionReward, settleAt, feeVoucherRewardRecord }) => {
      overlay.open(({ isOpen, close, unmount }) => (
        <ClaimModal
          completionReward={completionReward}
          distributionDate={settleAt}
          feeVoucherRewardRecord={feeVoucherRewardRecord}
          isOpen={isOpen}
          onClose={close}
          onExit={unmount}
        />
      ));
    },
    onError: (error) => {
      switch (getErrorCode(error)) {
        case 'api.unauthorized':
          webviewHandler.postMessage({ action: 'link', params: { page: 'login' } });
          break;
        case 'api.ineligible':
          overlay.open(({ isOpen, close, unmount }) => (
            <Dialog.Root isOpen={isOpen} onExit={unmount}>
              <Dialog.Title label={<FormattedMessage id='Reward_hub.r250213.Ineligible_for_reward_hub' />} />
              <Dialog.Content>
                <FormattedMessage id='Reward_hub.r250213.Ineligible_for_reward_hub_description' />
              </Dialog.Content>
              <Dialog.Footer>
                <Dialog.Button
                  hierarchy='primary'
                  label={<FormattedMessage id='Common.r250918.Got_it' />}
                  onClick={close}
                />
              </Dialog.Footer>
            </Dialog.Root>
          ));
          break;
        default:
          showErrorDialog(error);
      }
    },
  }));

  const rewardHubV2QueryAtom = atomWithQuery((get) => {
    return {
      queryKey: ['getRewardHubV2Api', get(isCookieLoggedInAtom)],
      queryFn: () => getRewardHubV2Api(),
      select: (res: AxiosResponse<GetRewardHubV2Response>) => res.data,
    };
  });

  const rewardHubV2DataAtom = atom((get) => get(rewardHubV2QueryAtom).data);
  const feeVoucherRewardAtom = atom(
    (get): Record<number, FeeVoucherReward> => get(rewardHubV2DataAtom)?.feeVoucherReward ?? {},
  );
  const isLoadingAtom = atom((get) => get(rewardHubV2QueryAtom).isLoading);

  const isAbuserAtom = atom((get) => get(rewardHubV2DataAtom)?.isAbuser);

  const boostMultiplierAtom = atom((get) => get(rewardHubV2DataAtom)?.extraData?.tradFiBoostMultiplier ?? '1.0');
  const isTradfiBoostEnabledAtom = atom((get) => Number(get(boostMultiplierAtom)) > 1);

  const skinAtom = atom((get) => get(rewardHubV2DataAtom)?.skin);
  const rewardSectionSkinsAtom = atom((get) => get(skinAtom)?.rewardSectionSkins ?? []);

  const specAtom = atom((get) => get(rewardHubV2DataAtom)?.spec);
  const missionStatusAtom = atom((get) => get(rewardHubV2DataAtom)?.missionStatus);

  const specialMissionAtom = atom((get) => {
    const spec = get(specAtom);
    const skin = get(skinAtom);
    const missionStatus = get(missionStatusAtom);
    const specialMissionStatuses = missionStatus?.specialMissionStatus ?? [];

    const allSpecialMissionsSpecs = spec?.specialMissions ?? [];
    const allSpecialMissionsSkins = skin?.specialMissionSkins ?? [];

    return allSpecialMissionsSpecs.map((specialMission, missionIndex) => ({
      spec: specialMission,
      skin: allSpecialMissionsSkins[missionIndex],
      status: specialMissionStatuses[missionIndex],
      sectionIndex: SpecialSectionIndex,
      missionIndex,
    }));
  });

  const rewardSectionsAtom = atom((get) => {
    const spec = get(specAtom);
    const sections = spec?.rewardSections ?? [];
    const skins = get(rewardSectionSkinsAtom);
    const missionStatus = get(missionStatusAtom);
    const missionStatuses = missionStatus?.sectionStatus ?? [];

    const regularMissionsSpecs = sections.map((section) => section.regularMissions);
    const regularMissionsSkins = skins.map((skin) => skin.regularMissionDescriptions);
    const regularMissionsStatuses = missionStatuses.map((status) => status.regularMissionStatus);

    const firstAssignedIndexPerSection = regularMissionsStatuses.map((statuses) =>
      (statuses ?? []).findIndex((status) => status?.itemStatus === 'ASSIGNED' && !status.isComplete),
    );

    return sections.map((section, sectionIndex) => {
      return {
        key: sectionIndex,
        ...section,
        sectionDescription: skins[sectionIndex]?.description,
        sectionStatus: missionStatuses[sectionIndex],
        regularMissions: regularMissionsSpecs[sectionIndex].map((regularMission, missionIndex) => ({
          sectionIndex,
          missionIndex,
          spec: regularMission,
          skin: regularMissionsSkins[sectionIndex]?.[missionIndex],
          status: regularMissionsStatuses[sectionIndex]?.[missionIndex],
          isActive: firstAssignedIndexPerSection[sectionIndex] === missionIndex,
        })),
      };
    });
  });

  const isShowRewardSectionAtom = atom((get) => {
    const sections = get(rewardSectionsAtom);
    return Boolean(sections.length);
  });

  const totalClaimedRewardAtom = atom((get) => {
    const spec = get(specAtom);
    const sections = spec?.rewardSections ?? [];
    const specialMissions = spec?.specialMissions ?? [];
    const missionStatus = get(missionStatusAtom);
    const missionStatuses = missionStatus?.sectionStatus ?? [];
    const specialMissionStatuses = missionStatus?.specialMissionStatus ?? [];

    const feeVoucherRewardRecord = get(feeVoucherRewardAtom);

    const regularRewards = sections.reduce((accumulatedTotal, section, sectionIndex) => {
      const sectionStatus = missionStatuses[sectionIndex];
      if (!sectionStatus) return accumulatedTotal;

      const settledRegularRewards = section.regularMissions
        .filter((_, missionIndex) => sectionStatus.regularMissionStatus[missionIndex]?.itemStatus === 'SETTLED')
        .flatMap((mission) => mission.completionRewards);

      return accumulatedTotal.plus(getTotalClaimedReward(settledRegularRewards, feeVoucherRewardRecord));
    }, Num('0'));

    const settledSpecialRewards = specialMissions
      .filter((_, missionIndex) => specialMissionStatuses[missionIndex]?.itemStatus === 'SETTLED')
      .flatMap((mission) => mission.speedBonus.completionRewards);

    const specialRewards = getTotalClaimedReward(settledSpecialRewards, feeVoucherRewardRecord);

    return regularRewards.plus(specialRewards);
  });

  return {
    rewardHubV2QueryAtom,
    claimRewardHubV2MutationAtom,
    isAbuserAtom,
    boostMultiplierAtom,
    isTradfiBoostEnabledAtom,
    isLoadingAtom,
    feeVoucherRewardAtom,
    specAtom,
    rewardSectionsAtom,
    specialMissionAtom,
    isShowRewardSectionAtom,
    skinAtom,
    rewardSectionSkinsAtom,
    missionStatusAtom,
    totalClaimedRewardAtom,
  };
});
rewardHubV2Bunja.debugLabel = 'rewardHubV2Bunja';

const getTotalClaimedReward = (
  completionRewards: CompletionReward[],
  feeVoucherRewardRecord?: Record<number, FeeVoucherReward>,
) =>
  completionRewards.reduce((acc, reward) => acc.plus(Num(getRewardAmount(reward, feeVoucherRewardRecord))), Num('0'));
